import { For, Show, createSignal, onCleanup, onMount } from "solid-js";
import { navigate } from "../core/router";

/** One line of the start menu; a locked one shows but cannot be chosen yet. */
export interface StartMenuItem { label: string; detail: string; href?: string; locked?: boolean }

interface StartPageProps {
  items: StartMenuItem[];
  version: string;
}

/**
 * The title screen: the name drafts itself in like a pattern line, then a
 * short description and a menu to jump into the app, driven by mouse or keys
 * (arrows or W/S to move, Enter or Space to choose, 1–9 to jump).
 */
export function StartPage(props: StartPageProps) {
  const firstOpen = () => Math.max(0, props.items.findIndex((item) => !item.locked));
  const [selected, setSelected] = createSignal(firstOpen());
  // Any key or click during the intro jumps to its end.
  const [skipped, setSkipped] = createSignal(false);
  const [introDone, setIntroDone] = createSignal(false);

  const move = (step: number) => {
    const count = props.items.length;
    let next = selected();
    for (let tries = 0; tries < count; tries += 1) {
      next = (next + step + count) % count;
      if (!props.items[next].locked) break;
    }
    setSelected(next);
  };
  const choose = (index: number) => {
    const item = props.items[index];
    if (item && !item.locked && item.href) navigate(item.href);
  };

  const onKey = (event: KeyboardEvent) => {
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    if (!introDone()) { setSkipped(true); setIntroDone(true); if (event.key !== "Tab") event.preventDefault(); return; }
    const key = event.key.toLowerCase();
    if (key === "arrowdown" || key === "s") { event.preventDefault(); move(1); }
    else if (key === "arrowup" || key === "w") { event.preventDefault(); move(-1); }
    else if (key === "enter" || key === " ") { event.preventDefault(); choose(selected()); }
    else if (/^[1-9]$/.test(key) && Number(key) <= props.items.length) {
      const index = Number(key) - 1;
      if (!props.items[index].locked) { setSelected(index); choose(index); }
    }
  };

  onMount(() => {
    window.addEventListener("keydown", onKey);
    // The menu takes keys once the intro's last item is in.
    const timer = window.setTimeout(() => setIntroDone(true), 2600);
    onCleanup(() => { window.removeEventListener("keydown", onKey); window.clearTimeout(timer); });
  });

  return (
    <main class="start-screen" classList={{ skipped: skipped() }} onPointerDown={() => { if (!introDone()) { setSkipped(true); setIntroDone(true); } }}>
      <div class="start-grid" aria-hidden="true" />
      <svg class="start-pieces" viewBox="0 0 1600 1000" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
        {/* A back, a front and a sleeve, loosely after the blocks, drifting behind the title. */}
        <g class="start-piece piece-a"><path d="M180 140 L330 120 Q360 125 380 150 L470 180 Q440 260 455 330 Q470 380 520 400 L520 640 L180 640 Z" /><path class="cut" d="M168 128 L330 106 Q366 112 390 140 L486 172 Q454 258 469 326 Q482 370 534 390 L534 654 L168 654 Z" /></g>
        <g class="start-piece piece-b"><path d="M1180 170 Q1230 230 1310 240 L1420 200 L1420 650 L1150 650 L1150 420 Q1200 400 1210 340 Q1190 260 1180 170 Z" /><path class="cut" d="M1166 156 Q1222 220 1310 226 L1434 184 L1434 664 L1136 664 L1136 410 Q1186 392 1196 340 Q1176 262 1166 156 Z" /></g>
        <g class="start-piece piece-c"><path d="M600 760 Q700 640 800 630 Q900 640 1000 760 L970 960 L630 960 Z" /><path class="cut" d="M584 760 Q694 624 800 616 Q906 624 1016 760 L984 974 L616 974 Z" /></g>
      </svg>
      <div class="start-scanlines" aria-hidden="true" />

      <div class="start-content">
        <p class="start-eyebrow">MADE-TO-MEASURE PATTERN CUTTING</p>
        <h1 class="start-title">
          <svg viewBox="0 0 1000 190" role="img" aria-label="Auto Draft">
            <defs>
              <linearGradient id="start-title-fill" x1="0" x2="1" y1="0" y2="0">
                <stop offset="0" stop-color="#e9c46a" />
                <stop offset="0.42" stop-color="#e9c46a" />
                <stop offset="0.5" stop-color="#fff6d8" />
                <stop offset="0.58" stop-color="#e9c46a" />
                <stop offset="1" stop-color="#e9c46a" />
                {/* A highlight that sweeps across the name every few seconds. */}
                <animateTransform attributeName="gradientTransform" type="translate" values="-1 0;1 0;1 0" keyTimes="0;0.45;1" dur="4.5s" begin="2s" repeatCount="indefinite" />
              </linearGradient>
            </defs>
            <text class="title-seam" x="500" y="138" text-anchor="middle">AUTO DRAFT</text>
            <text class="title-line" x="500" y="138" text-anchor="middle">AUTO DRAFT</text>
            <text class="title-fill" x="500" y="138" text-anchor="middle" fill="url(#start-title-fill)">AUTO DRAFT</text>
          </svg>
        </h1>
        <p class="start-description">Measure a body, pick a block from the classic cutting books, and Auto Draft drafts the pattern to fit, ready to print and cut.</p>

        <nav class="start-menu" aria-label="Start">
          <ul>
            <For each={props.items}>{(item, index) => (
              <li style={{ "--i": index() }}>
                <Show when={!item.locked} fallback={
                  <span class="start-item locked" aria-disabled="true"><span class="start-key">{index() + 1}</span><span class="start-label">{item.label}</span><span class="start-soon">SOON</span><small>{item.detail}</small></span>
                }>
                  <a
                    href={item.href}
                    class="start-item"
                    classList={{ selected: selected() === index() }}
                    aria-current={selected() === index() ? "true" : undefined}
                    onMouseEnter={() => setSelected(index())}
                    onFocus={() => setSelected(index())}
                    onClick={(event) => { if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey) return; event.preventDefault(); choose(index()); }}
                  >
                    <span class="start-cursor" aria-hidden="true">▶</span><span class="start-key">{index() + 1}</span><span class="start-label">{item.label}</span><small>{item.detail}</small>
                  </a>
                </Show>
              </li>
            )}</For>
          </ul>
        </nav>
        <p class="start-prompt"><span class="blink">PRESS ENTER</span></p>
      </div>

      <footer class="start-footer"><span>v{props.version}</span><span><kbd>↑</kbd><kbd>↓</kbd> choose <kbd>Enter</kbd> select</span></footer>
    </main>
  );
}
